import { createAsyncThunk } from '@reduxjs/toolkit'
import { isEmpty, map, omit, get, filter, findIndex, find, isEqual, some } from 'lodash'

import {
  LabelingObjectData,
  ThunkConfig,
  BoxObject,
  EllipseObject,
  PolygonObject,
  VideoSegmentObject
} from '@/stores/slices/Labeling/types'
import { convertToNormalCoordinates, convertToScaledCoordinates, getSettledFiles, serializeObject, transformAnnotObjects } from '@/stores/slices/Labeling/utils'

import { socket } from '@/socket'
import { updatePropmptImageIds, updateVideoSegmentMemo } from '@/stores/slices/Labeling/labelingSlice'

import { API } from '@/api'
import {
  LabelingNodeItem,
  LabelingObject,
  LabelingToolType,
  OBJECT_TOOL_TYPE,
  ROI_TOOL_TYPE,
  UpdateLabelAssistantPayload
} from '@/api/LabelingNode/types'
import { LABEL_ONGOING, LABELING_STATUS, LABELING_TOOL_TYPE } from '@/api/LabelingNode/const'
import { ANNOTATION_TYPE } from '@/pages/Labeling/types'
import { isMaskingOverlapError, openMaskingOverlapDialog } from '@/pages/Labeling/components/MaskingOverlapDialog'

interface LoadObjectsParams {
  index: number;
  isLabelAssistant?: boolean;
  /**
   * 불필요한 API 요청을 보내지 않기 위해 현재 이미지의 스냅샷이 존재하면 `loadAnnotObjects`를 호출하더라도 API 요청을 보내지 않음.
   * 클래스 삭제 등의 동작으로 기존 스냅샷이 유효하지 않은 상태가 되면 스냅샷이 존재하더라도 API 요청을 보낼 수 있도록 함.
   */
  invalidate?: boolean;
}

interface LoadObjectsResponse {
  objectData: LabelingObjectData[] | undefined,
  labelingItemId: string,
  labelingItemDetails: LabelingNodeItem,
  addSnapshot: boolean,
  trackedObjects?: LabelingObjectData[],
}

type UndoRedoActionPayload = {
  currentObjectsState: LabelingObject[] | null,
  prevObjectState: LabelingObject[],
  hasSemiAutoLabelingObjects: boolean,
}

type CommonPayload = { itemId: string, shapeIdx?: number, toolType?: LabelingToolType }
type BoxObjectPayload = Omit<BoxObject, 'modifiedAt' | 'createdAt'>
type EllipseObjectPayload = Omit<EllipseObject, 'modifiedAt' | 'createdAt'>
type PolygonObjectPayload = Omit<PolygonObject, 'modifiedAt' | 'createdAt'>
type VideoSegmentObjectPayload = Omit<VideoSegmentObject, 'modifiedAt' | 'createdAt'>
type AddObjectPayload = CommonPayload & (BoxObjectPayload | EllipseObjectPayload | PolygonObjectPayload | VideoSegmentObjectPayload)

type CreatedObject = { object: LabelingObjectData, itemId: string }

type UpdateCommonPayload = { itemId: string, objectId: string, text?: string }
type UpdateBoxPayload = {
  shape: typeof ANNOTATION_TYPE.BOX
  x: number
  y: number
  width: number
  height: number
  rotation: number
}
type UpdateEllipsePayload = {
  shape: typeof ANNOTATION_TYPE.ELLIPSE
  cx: number
  cy: number
  rx: number
  ry: number
  rotation: number
}
type UpdatePolygonPayload = { shape: typeof ANNOTATION_TYPE.POLYGON, points: { x: number, y: number }[] }
type UpdateVideoSegmentPayload = {
  shape: typeof ANNOTATION_TYPE.VIDEO_SEGMENT
  startMs: number
  endMs: number
  startFrameNumber: number
  endFrameNumber: number
  videoFileMetaId?: string | null
  memoContent?: string | null
}
export type UpdateObjectPayload = UpdateCommonPayload & (UpdateBoxPayload | UpdateEllipsePayload | UpdatePolygonPayload | UpdateVideoSegmentPayload)
export type UpdateVideoObjectPayload = UpdateCommonPayload & UpdateVideoSegmentPayload
type UpdatedObject = { itemId: string, object?: LabelingObjectData }
type UpdateObjectsClassPayload = { itemId: string, objectIds: string[], classId: string }

/**
 * load objects
 */
export const loadObjects = createAsyncThunk<LoadObjectsResponse, LoadObjectsParams, ThunkConfig>(
  'labeling/load-objects',
  async ({ index }, thunkAPI) => {
    const { user } = thunkAPI.getState().user
    const state = thunkAPI.getState().labeling
    const {
      labelingNodeId,
      currentIndex,
      files,
      originalFiles,
      annotation: prevSnapshot,
      tool: { stamp },
      userIsReviewer,
    } = state

    let trackedObjects

    const labelingItem = getSettledFiles(state, index)
    const labelingItemDetails = await API().Labeling.getFileDetails(labelingItem._id)

    // 현재 userMeta 사용안함
    await API().Labeling.updateUserMeta({ labelingNodeId, lastIndex: findIndex(originalFiles, { _id: labelingItem._id }), lastLabelingItemId: labelingItem._id, duration: 0 })

    if (!labelingNodeId) {
      return {
        objectData: [],
        labelingItemId: labelingItem._id,
        labelingItemDetails,
        addSnapshot: false,
      }
    }

    const stampConditionForReviewer = userIsReviewer && (labelingItemDetails?.status !== LABELING_STATUS.COMPLETED && labelingItemDetails?.status !== LABELING_STATUS.REJECTED)
    const stampConditionForNotReviewer = !userIsReviewer && LABEL_ONGOING.includes(labelingItemDetails?.status)

    if (stamp.isTracking && isEmpty(labelingItemDetails.labelingObjects)
      && (stampConditionForReviewer || stampConditionForNotReviewer)) {
      // 현재 이미지의 사이즈 (스탬프를 찍어낼 이미지)
      const { width, height } = get(labelingItemDetails, 'fileMeta.mediaInfo', { width: 0, height: 0 })

      // 스탬프를 켠 이미지 (이전 이미지)
      const prevLabelingItem = files[currentIndex]
      const prevLabelingItemId = get(prevLabelingItem, '_id', '')
      const { width: prevWidth, height: prevHeight } = get(prevLabelingItem, 'fileMeta.mediaInfo', { width: 0, height: 0 })
      const prevObjects = filter(prevSnapshot[prevLabelingItemId].object, (o) => !o.roiToolType)
      const prevScaled = convertToScaledCoordinates(prevObjects, prevWidth, prevHeight)

      // 현재 이미지의 사이즈에 맞게 스탬프 오브젝트를 실 좌표로 변환
      const normalAnnotationInfo = convertToNormalCoordinates(prevScaled, width, height)
      const filteredComments = filter(normalAnnotationInfo, (o) => o.t !== undefined)
      const objectsToSave = map(filteredComments, (o) => ({ ...omit(
        serializeObject({
          o,
          itemId: '',
          tagOrder: o.name,
          includeText: stamp.includeText,
        }),
        ['labelingItemId', 'labelingObjectId']
      ),
      toolType: LABELING_TOOL_TYPE.STAMP,
      }))
      const { data: savedObjects } = await API().Labeling.saveObjects({ labelingItemId: labelingItem._id, labelingObjects: objectsToSave })

      trackedObjects = savedObjects

      socket?.Labeling.emitCreateObject({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId: labelingItem._id,
        createdObjects: savedObjects,
      })
    }

    return {
      objectData: transformAnnotObjects(labelingItemDetails.labelingObjects),
      labelingItemId: labelingItem._id,
      labelingItemDetails,
      addSnapshot: true,
      trackedObjects: transformAnnotObjects(trackedObjects || []),
    }

    // NOTE: 지워도 될듯 함 + 추가확인 해서 문제없으면 지우기 (옛날에 UI에서 undo/redo 하던 흔적같음)
    // if (file._id in prevSnapshot) {
    //   return {
    //     objectData: transformAnnotObjects(fetchedFile.labelingObjects),
    //     settledFile: file,
    //     fetchedFile,
    //     addSnapshot: false,
    //     trackedObjects: transformAnnotObjects(trackedObjects || []),
    //   }
    // }
  },
  {
    condition: ({ index }, { getState }) => {
      const state = getState().labeling
      if (state.isInProgress && state.tool.stamp.isTracking) {
        return false
      }
    },
  }
)

/**
 * create Objects
 */
export const addObject = createAsyncThunk<CreatedObject, AddObjectPayload, ThunkConfig>(
  'labeling/add-object',
  async (payload, thunkAPI) => {
    const { itemId, shapeIdx = 0, toolType, t: shapeType, ...objectProps } = payload
    const now = new Date()
    const { user } = thunkAPI.getState().user
    const { tool: { class: currentClass, isMaskingMode }, labelingNodeId, annotation, files, currentIndex } = thunkAPI.getState().labeling
    const fileId = files[currentIndex]?._id
    const allObjects = fileId ? annotation[fileId].object : []
    // NOTE: 학습 제외 영역(마스킹)은 오브젝트 인덱스 카운팅에서 제외한다
    const nonMaskingObjectCount = filter(allObjects, (o) => o.roiToolType !== ROI_TOOL_TYPE.MASKING).length
    const tagOrder = nonMaskingObjectCount + 1 + shapeIdx

    let shapeProperties: Partial<LabelingObjectData>

    switch (shapeType) {
      case ANNOTATION_TYPE.BOX: {
        const { x, y, width, height, isFixedRatio, rotation } = objectProps as BoxObjectPayload
        shapeProperties = { x, y, width, height, isFixedRatio, rotation }
        break
      }
      case ANNOTATION_TYPE.ELLIPSE: {
        const { id, cx, cy, rx, ry, isFixedRatio, rotation } = objectProps as EllipseObjectPayload
        shapeProperties = { id, cx, cy, rx, ry, isFixedRatio, rotation }
        break
      }
      case ANNOTATION_TYPE.POLYGON: {
        const { points, isPathEditable } = objectProps as PolygonObjectPayload
        shapeProperties = { points, isPathEditable }
        break
      }
      case ANNOTATION_TYPE.VIDEO_SEGMENT: {
        const {
          startMs,
          endMs,
          videoFileId,
          videoFileMetaId,
          startFrameNumber,
          endFrameNumber,
          memoContent,
          color,
        } = objectProps as VideoSegmentObjectPayload
        shapeProperties = {
          startMs,
          endMs,
          videoFileId,
          videoFileMetaId,
          startFrameNumber,
          endFrameNumber,
          memoContent,
          color,
        }
        break
      }
      default:
        throw new Error('Unsupported shape type')
    }

    // 제외 영역 모드에서 그린 오브젝트는 클래스 없이 마스킹 오브젝트로 생성한다
    const createdObject = {
      ...shapeProperties,
      t: shapeType,
      label: isMaskingMode ? '' : ((objectProps as any).label ?? currentClass),
      roiToolType: isMaskingMode ? ROI_TOOL_TYPE.MASKING : undefined,
      modifiedAt: now,
      createdAt: now,
      name: tagOrder,
      zOrder: tagOrder,
      toolType,
    } as LabelingObjectData

    try {
      if (shapeType === ANNOTATION_TYPE.VIDEO_SEGMENT) {
        const videoSegment = createdObject as VideoSegmentObject
        const res = await API().Labeling.createVideoObject({
          labelingItemId: itemId,
          videoFileMetaId: videoSegment.videoFileMetaId ?? '',
          type: OBJECT_TOOL_TYPE.VIDEO_SEGMENT,
          startMs: videoSegment.startMs,
          endMs: videoSegment.endMs,
          labelingClassId: videoSegment.label ?? undefined,
          metadata: {
            createdAt: now,
            modifiedAt: now,
            tagOrder,
            startFrameNumber: videoSegment.startFrameNumber,
            endFrameNumber: videoSegment.endFrameNumber,
            memoContent: videoSegment.memoContent,
          },
        })
        socket?.Labeling.emitCreateObject({
          nodeId: labelingNodeId,
          userId: user._id,
          userName: user.name,
          itemId,
          createdObjects: [res],
        } as any)
        return { object: transformAnnotObjects([res])[0], itemId }
      }

      const res = await API().Labeling.createObject({ ...serializeObject({ o: createdObject, itemId, tagOrder }), toolType })
      socket?.Labeling.emitCreateObject({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId,
        createdObjects: [res],
      } as any)
      return { object: { ...createdObject, id: res._id }, itemId }
    } catch (error) {
      // 라벨링 오브젝트와 학습 제외 영역이 겹치는 경우 안내 다이얼로그 노출
      if (isMaskingOverlapError(error)) {
        openMaskingOverlapDialog()
      }
      throw error
    }
  }
)

/**
 * update objects
 */
export const updateObject = createAsyncThunk<UpdatedObject, UpdateObjectPayload, ThunkConfig>(
  'labeling/update-object',
  async (payload, thunkAPI) => {
    const { itemId, objectId, shape: shapeType, text, ...objectProps } = payload
    const { user } = thunkAPI.getState().user
    const { labelingNodeId, annotation } = thunkAPI.getState().labeling
    const { object } = annotation[itemId]
    const prevObject = find(object, (o) => o.id === objectId)

    if (!prevObject) {
      return { itemId }
    }

    let shapeProperties: Partial<LabelingObjectData>

    switch (shapeType) {
      case ANNOTATION_TYPE.BOX: {
        const { x, y, width, height, rotation } = objectProps as UpdateBoxPayload
        shapeProperties = { x, y, width, height, rotation }
        break
      }
      case ANNOTATION_TYPE.ELLIPSE: {
        const { cx, cy, rx, ry, rotation } = objectProps as UpdateEllipsePayload
        shapeProperties = { cx, cy, rx, ry, rotation }
        break
      }
      case ANNOTATION_TYPE.POLYGON: {
        const { points } = objectProps as UpdatePolygonPayload
        shapeProperties = { points }
        break
      }
      default:
        return { itemId }
    }

    const updatedObject = {
      ...prevObject,
      ...shapeProperties,
      modifiedAt: new Date(),
      toolType: LABELING_TOOL_TYPE.USER,
    } as LabelingObjectData

    try {
      await API().Labeling.updateObject({ ...serializeObject({ o: updatedObject, itemId, tagOrder: prevObject.name }), text })
    } catch (error) {
      // 라벨링 오브젝트와 학습 제외 영역이 겹치는 경우 안내 다이얼로그 노출 (rejected 리듀서에서 원래 위치로 롤백됨)
      if (isMaskingOverlapError(error)) {
        openMaskingOverlapDialog()
      }
      throw error
    }
    socket?.Labeling.emitEditObject({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      itemId,
      updatedObject,
    } as any)
    return { itemId, object: updatedObject }
  }
)

export const updateVideoObject = createAsyncThunk<UpdatedObject, UpdateVideoObjectPayload, ThunkConfig>(
  'labeling/update-video-object',
  async (payload, thunkAPI) => {
    const { itemId, objectId, startMs, endMs, videoFileMetaId = null, startFrameNumber, endFrameNumber, memoContent = null } = payload
    const { user } = thunkAPI.getState().user
    const { labelingNodeId, annotation } = thunkAPI.getState().labeling
    const { object } = annotation[itemId]
    const prevObject = find(object, (o) => o.id === objectId) as VideoSegmentObject | undefined

    if (!prevObject) {
      return { itemId }
    }

    const { label: labelingClassId } = prevObject

    if (!labelingClassId || !videoFileMetaId) {
      throw new Error('Missing video segment update payload')
    }

    const updatedObject = {
      ...prevObject,
      startMs,
      endMs,
      videoFileMetaId,
      startFrameNumber,
      endFrameNumber,
      memoContent,
      modifiedAt: new Date(),
      toolType: LABELING_TOOL_TYPE.USER,
    } as VideoSegmentObject

    await API().Labeling.updateVideoObject({
      labelingItemId: itemId,
      labelingObjectId: objectId,
      videoFileMetaId,
      type: OBJECT_TOOL_TYPE.VIDEO_SEGMENT,
      startMs,
      endMs,
      labelingClassId,
      metadata: {
        ...(prevObject.metadata ?? {}),
        tagOrder: prevObject.name,
        startFrameNumber,
        endFrameNumber,
        memoContent,
      },
    })

    socket?.Labeling.emitEditObject({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      itemId,
      updatedObject,
    } as any)
    return { itemId, object: updatedObject }
  }
)

export const updateObjectsClass = createAsyncThunk<{ itemId: string, objects: LabelingObjectData[] }, UpdateObjectsClassPayload, ThunkConfig>(
  'labeling/update-objects-class',
  async (payload, thunkAPI) => {
    const { itemId, objectIds, classId } = payload
    const { user } = thunkAPI.getState().user
    const { labelingNodeId, annotation } = thunkAPI.getState().labeling
    const { object } = annotation[itemId]

    const currentObjects = map(objectIds, (id) => find(object, { id }) as LabelingObjectData)
    const updatedObjects = map(currentObjects, (o) => ({ ...o, label: classId, modifiedAt: new Date(), toolType: LABELING_TOOL_TYPE.USER }) as LabelingObjectData)
    const objectsToSave = map(updatedObjects, (o) => serializeObject({ o, itemId, tagOrder: o.name }))
    await API().Labeling.saveObjects({ labelingItemId: itemId, labelingObjects: objectsToSave })
    socket?.Labeling.emitChangeObjectClass({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      itemId,
      updatedObjects,
    })
    return { itemId, objects: updatedObjects }
  }
)

export const updateLabelAssistant = createAsyncThunk<{ item: LabelingNodeItem, hasAutoLabelingObjects: boolean }, UpdateLabelAssistantPayload, ThunkConfig>(
  'labeling/update-label-assistant',
  async (payload, thunkAPI) => {
    try {
      const { labelingNodeId, promptImageIds, currentIndex } = thunkAPI.getState().labeling
      const { user } = thunkAPI.getState().user

      socket?.Labeling.emitStartLabelAssistant({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId: payload.labelingItemId,
      })

      const res = await API().Labeling.updateLabelAssistant(payload)

      if (!isEqual(promptImageIds, payload.labelingPromptItemIds)) {
        socket?.Labeling.emitLabelAssistantPromptChanged({
          nodeId: labelingNodeId,
          userId: user._id,
          userName: user.name,
        })
      }

      thunkAPI.dispatch(updatePropmptImageIds(payload.labelingPromptItemIds))

      socket?.Labeling.emitFinishLabelAssistant({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId: payload.labelingItemId,
        hasAutoLabelingObjects: res.hasAutoLabelingObjects,
        currentIndex,
      })

      return { item: res.labelingItem, hasAutoLabelingObjects: res.hasAutoLabelingObjects }
    } catch (e: any) {
      // axios 에러에서 unique_code 추출
      const uniqueCode = e?.response?.data?.unique_code
      const errorMessage = e?.response?.data?.message || e.message

      return thunkAPI.rejectWithValue({
        uniqueCode,
        message: errorMessage,
      })
    }
  }
)

/**
 * remove objects
 */
export const removeObjects = createAsyncThunk<{ itemId: string, objectIds: string[], hasSemiAutoLabelingObjects: boolean }, { itemId: string, objectIds: string[] }, ThunkConfig>(
  'labeling/remove-objects',
  async (payload, thunkAPI) => {
    const { itemId, objectIds } = payload
    const { user } = thunkAPI.getState().user
    const { labelingNodeId, annotation } = thunkAPI.getState().labeling
    const prevSnapshot = annotation[itemId]
    const { data: res } = await API().Labeling.deleteObjects({ labelingItemId: itemId, labelingObjectIds: objectIds })

    const objectsToDelete: LabelingObjectData[] = filter(prevSnapshot.object, (o): o is LabelingObjectData => o.id !== undefined && objectIds.includes(o.id))
    const hasSemiAutoLabelingObjects = some(objectsToDelete, (o) => o.toolType === LABELING_TOOL_TYPE.SEMI_AUTO_LABELING)

    socket?.Labeling.emitDeleteObject({
      nodeId: labelingNodeId,
      userId: user._id,
      userName: user.name,
      itemId,
      deletedObjects: res,
    } as any)
    return { itemId, objectIds, hasSemiAutoLabelingObjects }
  }
)

export const removeAllObjectsInItem = createAsyncThunk<
  { itemId: string, hasSemiAutoLabelingObjects: boolean },
  { itemId: string }, ThunkConfig>(
    'labeling/remove-objects-in-item',
    async (payload, thunkAPI) => {
      const { itemId } = payload

      const { user } = thunkAPI.getState().user
      const { labelingNodeId, annotation } = thunkAPI.getState().labeling
      const prevSnapshot = annotation[itemId]

      const res = await API().Labeling.deleteAllObjectsInItem(itemId)
      socket?.Labeling.emitDeleteAllObjects({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId,
      } as any)

      return {
        itemId,
        hasSemiAutoLabelingObjects: some(prevSnapshot.object, (o) => o.toolType === LABELING_TOOL_TYPE.SEMI_AUTO_LABELING),
      }
    }
  )

/**
 * undo - redo action
 */
export const undoAction = createAsyncThunk<UndoRedoActionPayload | null, void, ThunkConfig>(
  'labeling/undo-action',
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState().user
    const { currentIndex, files, labelingNodeId } = thunkAPI.getState().labeling
    const currentItemId = get(files[currentIndex], '_id', '')

    try {
      const { currentObjects, labelingHistory } = await API().Labeling.undoAction(currentItemId)
      socket?.Labeling.emitHandleUndo({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId: currentItemId,
        currentObjectsState: currentObjects,
        prevObjectState: labelingHistory.backupObjects,
      } as any)

      return {
        currentObjectsState: currentObjects,
        prevObjectState: labelingHistory.backupObjects,
        hasSemiAutoLabelingObjects: some(labelingHistory.backupObjects, (o) => o.toolType === LABELING_TOOL_TYPE.SEMI_AUTO_LABELING),
      }
    } catch (e) {
      // undo 결과가 라벨링 오브젝트와 학습 제외 영역이 겹치는 상태가 되는 경우 안내 다이얼로그 노출
      if (isMaskingOverlapError(e)) {
        openMaskingOverlapDialog()
      }
      console.error(e)
    }
    return null
  }
)

export const redoAction = createAsyncThunk<UndoRedoActionPayload | null, void, ThunkConfig>(
  'labeling/redo-action',
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState().user
    const { currentIndex, files, labelingNodeId } = thunkAPI.getState().labeling
    const currentItemId = get(files[currentIndex], '_id', '')
    try {
      const { currentObjects, labelingHistory } = await API().Labeling.redoAction(currentItemId)
      socket?.Labeling.emitHandleRedo({
        nodeId: labelingNodeId,
        userId: user._id,
        userName: user.name,
        itemId: currentItemId,
        currentObjectsState: currentObjects,
        prevObjectState: labelingHistory.backupObjects,
      } as any)

      return {
        currentObjectsState: currentObjects,
        prevObjectState: labelingHistory.backupObjects,
        hasSemiAutoLabelingObjects: some(labelingHistory.backupObjects, (o) => o.toolType === LABELING_TOOL_TYPE.SEMI_AUTO_LABELING),
      }
    } catch (e) {
      // redo 결과가 라벨링 오브젝트와 학습 제외 영역이 겹치는 상태가 되는 경우 안내 다이얼로그 노출
      if (isMaskingOverlapError(e)) {
        openMaskingOverlapDialog()
      }
      console.error(e)
    }
    return null
  }
)

export const saveVideoSegmentMemo = createAsyncThunk<
  void,
  { itemId: string, objectId: string, memoContent: string },
  ThunkConfig
>(
  'labeling/save-video-segment-memo',
  async ({ itemId, objectId, memoContent }, thunkAPI) => {
    await API().Labeling.updateObjectMemo(objectId, memoContent)
    thunkAPI.dispatch(updateVideoSegmentMemo({ itemId, objectId, memoContent }))
  }
)
